import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import LoadingState from './LoadingState';
import PromptList from './PromptList';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { callOpenRouter } from '../utils/openRouter';

const { FiInfo, FiMusic, FiAlertCircle, FiSearch, FiRefreshCw } = FiIcons;

const YouTubeAnalyzer = ({ settings, onRequireSettings }) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [prompts, setPrompts] = useState([]);
  const [error, setError] = useState(null);
  const [songName, setSongName] = useState('');

  const handleAnalyze = async (e) => {
    if (e) e.preventDefault();
    
    if (!settings.apiKey) {
      onRequireSettings();
      setError("Please add your OpenRouter API key first.");
      return;
    }

    if (!songName.trim()) {
      setError("Please provide the song name or artist for better analysis.");
      return;
    }

    setError(null);
    setPrompts([]);
    setIsAnalyzing(true);

    try {
      const results = await callOpenRouter(settings.apiKey, settings.model, songName);
      setPrompts(results);
    } catch (err) {
      setError(err.message || "Failed to analyze song. Check your API key and model ID.");
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div className="w-full max-w-3xl flex flex-col items-center">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-stone-800 mb-2">Reference Track Analyzer</h2>
        <p className="text-stone-500">Extract musical characteristics to build 10 varied prompts.</p>
      </div>

      <div className="w-full space-y-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-orange-100/50 border border-orange-200 rounded-xl p-4 flex items-start gap-3 text-sm text-stone-600"
        >
          <SafeIcon icon={FiInfo} className="text-orange-500 text-lg flex-shrink-0 mt-0.5" />
          <p>
            <strong>How it works:</strong> Enter a well-known song and artist, and the AI will retrieve its musical characteristics (Genre, Mood, Instruments) from its knowledge base to generate accurate Suno prompts.
          </p>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="relative w-full"
        >
          <form onSubmit={handleAnalyze} className="relative group w-full">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400 group-focus-within:text-orange-500 transition-colors">
              <SafeIcon icon={FiMusic} className="text-2xl" />
            </div>
            
            <input 
              type="text" 
              value={songName} 
              onChange={(e) => setSongName(e.target.value)}
              disabled={isAnalyzing}
              placeholder="Song Name & Artist (e.g. 'Blinding Lights by The Weeknd')" 
              className="w-full pl-12 pr-40 py-4 rounded-2xl border-2 border-orange-100 bg-white/80 backdrop-blur-sm shadow-sm focus:border-orange-500 focus:ring-4 focus:ring-orange-500/20 transition-all text-stone-700 text-lg placeholder-stone-400 disabled:opacity-70 disabled:cursor-not-allowed"
            />
            
            <button 
              type="submit" 
              disabled={isAnalyzing || !songName.trim()}
              className="absolute right-2 top-2 bottom-2 px-6 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-xl font-bold shadow-lg shadow-orange-200 hover:shadow-orange-300 hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-70 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isAnalyzing ? (
                <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }}>
                  <SafeIcon icon={FiRefreshCw} />
                </motion.div>
              ) : (
                <>
                  <span>Analyze</span>
                  <SafeIcon icon={FiSearch} />
                </>
              )}
            </button>
          </form>
        </motion.div>
      </div>

      {error && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mt-6 p-4 w-full bg-red-50 border border-red-100 text-red-600 rounded-2xl flex items-center gap-3"
        >
          <SafeIcon icon={FiAlertCircle} className="text-xl flex-shrink-0" />
          <p className="text-sm font-medium">{error}</p>
        </motion.div>
      )}

      <AnimatePresence mode="wait">
        {isAnalyzing && (
          <motion.div key="loading" className="w-full">
            <LoadingState isVisible={isAnalyzing} />
          </motion.div>
        )}
        
        {prompts.length > 0 && !isAnalyzing && (
          <motion.div key="results" className="w-full">
            <PromptList prompts={prompts} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default YouTubeAnalyzer;